import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Account } from '../types';
import { getAccountTypeDisplayName } from '../services/categoryService';

const Transfers: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = window.innerWidth <= 768;
  const [formData, setFormData] = useState({
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/accounts');
      setAccounts(response.data.data.accounts);
    } catch (error) {
      console.error('Fehler beim Laden der Konten:', error);
      setError('Fehler beim Laden der Konten');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.fromAccountId || !formData.toAccountId) {
      setError('Bitte wählen Sie beide Konten aus');
      return;
    }

    if (formData.fromAccountId === formData.toAccountId) {
      setError('Quell- und Zielkonto müssen unterschiedlich sein');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Bitte geben Sie einen gültigen Betrag ein');
      return;
    }

    try {
      await api.post('/transfers', formData);
      setSuccess('Transfer erfolgreich durchgeführt!');
      
      // Formular zurücksetzen
      setFormData({
        fromAccountId: '',
        toAccountId: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: ''
      });

      // Konten neu laden
      fetchAccounts();

      // Erfolg-Nachricht nach 3 Sekunden ausblenden
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Fehler beim Transfer:', error);
      setError(error.response?.data?.message || 'Fehler beim Transfer');
    }
  };

  const getAccountBalance = (accountId: string): number => {
    const account = accounts.find(a => a.id === accountId);
    return account ? parseFloat(account.balance.toString()) : 0;
  };

  const getAccountName = (accountId: string): string => {
    const account = accounts.find(a => a.id === accountId);
    return account ? account.name : '';
  };

  if (loading) {
    return <div className="loading">Lädt...</div>;
  }

  return (
    <div className="container">
      <div style={{
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        borderRadius: '16px',
        padding: '30px',
        marginBottom: '30px',
        color: 'white',
        boxShadow: '0 8px 20px rgba(15, 32, 39, 0.3)'
      }}>
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          💸 Konto-Transfer
        </h1>
      </div>

      {error && (
        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div style={{
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>✅</span>
          <span>{success}</span>
        </div>
      )}

      <div className="card" style={{
        borderRadius: '16px',
        border: 'none',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
      }}>
        <form onSubmit={handleSubmit}>
          <div className="form-row" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '20px'
          }}>
            <div className="form-group">
              <label style={{ 
                fontWeight: '600', 
                color: '#2c5364',
                marginBottom: '8px',
                display: 'block',
                fontSize: '14px'
              }}>
                Von Konto *
              </label>
              <select
                value={formData.fromAccountId}
                onChange={(e) => setFormData({ ...formData, fromAccountId: e.target.value })}
                required
                style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef',
                  fontSize: '15px',
                  transition: 'all 0.3s ease',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Konto auswählen...</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({parseFloat(account.balance.toString()).toFixed(2)} €)
                  </option>
                ))}
              </select>
              {formData.fromAccountId && (
                <div style={{ 
                  background: getAccountBalance(formData.fromAccountId) >= 0 ? 'rgba(17, 153, 142, 0.1)' : 'rgba(240, 147, 251, 0.1)',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  marginTop: '8px',
                  fontSize: '13px',
                  color: '#495057',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>Aktueller Saldo:</span>
                  <strong style={{ color: getAccountBalance(formData.fromAccountId) >= 0 ? '#11998e' : '#f5576c' }}>
                    {getAccountBalance(formData.fromAccountId).toFixed(2)} €
                  </strong>
                </div>
              )}
            </div>

            <div className="form-group">
              <label style={{ 
                fontWeight: '600', 
                color: '#2c5364',
                marginBottom: '8px',
                display: 'block',
                fontSize: '14px'
              }}>
                Nach Konto *
              </label>
              <select
                value={formData.toAccountId}
                onChange={(e) => setFormData({ ...formData, toAccountId: e.target.value })}
                required
                style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef',
                  fontSize: '15px',
                  transition: 'all 0.3s ease',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Konto auswählen...</option>
                {accounts
                  .filter(account => account.id !== formData.fromAccountId)
                  .map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({parseFloat(account.balance.toString()).toFixed(2)} €)
                    </option>
                  ))}
              </select>
              {formData.toAccountId && (
                <div style={{ 
                  background: getAccountBalance(formData.toAccountId) >= 0 ? 'rgba(17, 153, 142, 0.1)' : 'rgba(240, 147, 251, 0.1)',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  marginTop: '8px',
                  fontSize: '13px',
                  color: '#495057',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>Aktueller Saldo:</span>
                  <strong style={{ color: getAccountBalance(formData.toAccountId) >= 0 ? '#11998e' : '#f5576c' }}>
                    {getAccountBalance(formData.toAccountId).toFixed(2)} €
                  </strong>
                </div>
              )}
            </div>
          </div>

          <div className="form-row" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '20px'
          }}>
            <div className="form-group">
              <label style={{ 
                fontWeight: '600', 
                color: '#2c5364',
                marginBottom: '8px',
                display: 'block',
                fontSize: '14px'
              }}>
                Betrag (€) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                required
                style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef',
                  fontSize: '18px',
                  fontWeight: '600',
                  textAlign: 'right',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>

            <div className="form-group">
              <label style={{ 
                fontWeight: '600', 
                color: '#2c5364',
                marginBottom: '8px',
                display: 'block',
                fontSize: '14px'
              }}>
                Datum
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef',
                  fontSize: '15px',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label style={{ 
              fontWeight: '600', 
              color: '#2c5364',
              marginBottom: '8px',
              display: 'block',
              fontSize: '14px'
            }}>
              Beschreibung (optional)
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="z.B. Monatliche Sparrate"
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                border: '2px solid #e9ecef',
                fontSize: '15px',
                transition: 'all 0.3s ease'
              }}
            />
          </div>

          {formData.fromAccountId && formData.toAccountId && formData.amount && (
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(15, 32, 39, 0.05) 0%, rgba(44, 83, 100, 0.05) 100%)',
              padding: '20px', 
              borderRadius: '16px', 
              marginBottom: '25px',
              border: '2px solid rgba(44, 83, 100, 0.2)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              <h4 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '16px', 
                color: '#2c5364',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>📊</span>
                Vorschau
              </h4>
              <div style={{ fontSize: '15px', color: '#212529' }}>
                <div style={{ 
                  marginBottom: '15px',
                  background: 'rgba(240, 147, 251, 0.1)',
                  padding: '15px',
                  borderRadius: '12px',
                  border: '1px solid rgba(240, 147, 251, 0.2)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    <span style={{ fontSize: '18px' }}>📤</span>
                    {getAccountName(formData.fromAccountId)}
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '14px'
                  }}>
                    <span style={{ color: '#6c757d' }}>Aktuell:</span>
                    <span style={{ fontWeight: '600' }}>{getAccountBalance(formData.fromAccountId).toFixed(2)} €</span>
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '8px 0',
                    color: '#f5576c',
                    fontWeight: '600'
                  }}>
                    ↓ -{parseFloat(formData.amount).toFixed(2)} €
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '14px',
                    paddingTop: '8px',
                    borderTop: '1px dashed rgba(240, 147, 251, 0.3)'
                  }}>
                    <span style={{ color: '#6c757d' }}>Danach:</span>
                    <span style={{ fontWeight: '700', fontSize: '16px', color: '#f5576c' }}>
                      {(getAccountBalance(formData.fromAccountId) - parseFloat(formData.amount)).toFixed(2)} €
                    </span>
                  </div>
                </div>

                <div style={{ 
                  background: 'rgba(17, 153, 142, 0.1)',
                  padding: '15px',
                  borderRadius: '12px',
                  border: '1px solid rgba(17, 153, 142, 0.2)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    <span style={{ fontSize: '18px' }}>📥</span>
                    {getAccountName(formData.toAccountId)}
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '14px'
                  }}>
                    <span style={{ color: '#6c757d' }}>Aktuell:</span>
                    <span style={{ fontWeight: '600' }}>{getAccountBalance(formData.toAccountId).toFixed(2)} €</span>
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '8px 0',
                    color: '#11998e',
                    fontWeight: '600'
                  }}>
                    ↑ +{parseFloat(formData.amount).toFixed(2)} €
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '14px',
                    paddingTop: '8px',
                    borderTop: '1px dashed rgba(17, 153, 142, 0.3)'
                  }}>
                    <span style={{ color: '#6c757d' }}>Danach:</span>
                    <span style={{ fontWeight: '700', fontSize: '16px', color: '#11998e' }}>
                      {(getAccountBalance(formData.toAccountId) + parseFloat(formData.amount)).toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button 
            type="submit" 
            style={{ 
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(15, 32, 39, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(15, 32, 39, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(15, 32, 39, 0.3)';
            }}
          >
            <span style={{ fontSize: '20px' }}>🚀</span>
            Transfer durchführen
          </button>
        </form>
      </div>

      <div className="card" style={{ 
        marginTop: '20px',
        borderRadius: '16px',
        border: 'none'
      }}>
        <h3 style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px',
          color: '#2c5364',
          marginBottom: '20px'
        }}>
          <span style={{ fontSize: '24px' }}>📋</span>
          Ihre Konten
        </h3>
        {accounts.length === 0 ? (
          <p>Keine Konten vorhanden.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Konto</th>
                <th>Typ</th>
                <th>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td data-label="Konto">{account.name}</td>
                  <td data-label="Typ">{getAccountTypeDisplayName(account.type)}</td>
                  <td data-label="Saldo" style={{ 
                    fontWeight: 'bold',
                    color: parseFloat(account.balance.toString()) >= 0 ? '#28a745' : '#dc3545'
                  }}>
                    {parseFloat(account.balance.toString()).toFixed(2)} €
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Transfers;
